import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../general/Header";
import Aside from "../../general/Aside";
import Footer from "../../general/Footer";
import axios from '../../../axios/axiosconf';
import { Link } from "react-router-dom";
import Loading from "../../general/Loading";
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title);

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const FALLBACK_COLOR_ESTADO_SMILE = {
    0: '#12B8B8',
    1: '#FFCD00',
    2: '#7ECC38',
    3: '#F12B38',
    4: '#808080',
    5: '#00CDF8',
    6: '#BE61FC',
    7: '#ADFF2F',
};

function hoyISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function EstadisticasAgenda() {
    const hoy = hoyISO();
    const [loggedIn, setLoggedIn] = useState(false);
    const [client, setClient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [agendas, setAgendas] = useState([]);
    const [estadosCita, setEstadosCita] = useState([]);
    const [fechaInicio, setFechaInicio] = useState(hoy);
    const [fechaFin, setFechaFin] = useState(hoy);
    const [agendasSeleccionadas, setAgendasSeleccionadas] = useState([]);
    const [estadosSeleccionados, setEstadosSeleccionados] = useState([]);
    const [catalogosListos, setCatalogosListos] = useState(false);
    const [porEstado, setPorEstado] = useState([]);
    const [porMes, setPorMes] = useState([]);
    const [totalCitas, setTotalCitas] = useState(0);

    const getAuthConfig = useCallback(() => {
        const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
        return {
            headers: { Authorization: 'Bearer ' + userdata.access_token },
        };
    }, []);

    const cargarCatalogos = useCallback(() => {
        const config = getAuthConfig();
        const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');

        axios.get('/get-client?uid=' + userdata.user_id, config)
            .then((response) => {
                if (response.status === 200) {
                    setClient(response.data);
                    setLoggedIn(true);
                }
            })
            .catch(() => setLoggedIn(true));

        axios.get('/estados-cita?uid=' + userdata.user_id, config)
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    setEstadosCita(response.data);
                }
            })
            .catch((error) => console.error(error));

        axios.get('/agendas-usuario', config)
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    setAgendas(response.data);
                }
            })
            .catch((error) => console.error(error));
    }, [getAuthConfig]);

    const estadosUnicos = useMemo(() => {
        const map = new Map();
        estadosCita.forEach((estado) => {
            const sm = String(estado.estado_smile);
            if (!map.has(sm)) {
                map.set(sm, estado);
            }
        });
        return Array.from(map.values());
    }, [estadosCita]);

    const cargarEstadisticas = useCallback(() => {
        if (!fechaInicio || !fechaFin) return;

        setLoading(true);
        const config = {
            ...getAuthConfig(),
            params: {
                fecha_inicio: fechaInicio,
                fecha_fin: fechaFin,
            },
        };

        if (agendas.length > 0 && agendasSeleccionadas.length > 0 && agendasSeleccionadas.length < agendas.length) {
            config.params.agendas = agendasSeleccionadas.join(',');
        }
        if (estadosUnicos.length > 0 && estadosSeleccionados.length > 0 && estadosSeleccionados.length < estadosUnicos.length) {
            config.params.estados_smile = estadosSeleccionados.join(',');
        }

        axios.get('/estadisticas-agenda', config)
            .then((response) => {
                if (response.status === 200) {
                    setPorEstado(Array.isArray(response.data.por_estado) ? response.data.por_estado : []);
                    setPorMes(Array.isArray(response.data.por_mes) ? response.data.por_mes : []);
                    setTotalCitas(response.data.total || 0);
                }
            })
            .catch((error) => {
                console.error(error);
                alert('No se pudieron cargar las estadísticas de agenda');
            })
            .finally(() => setLoading(false));
    }, [agendas, agendasSeleccionadas, estadosSeleccionados, estadosUnicos.length, fechaFin, fechaInicio, getAuthConfig]);

    useEffect(() => {
        cargarCatalogos();
    }, [cargarCatalogos]);

    useEffect(() => {
        if (agendas.length > 0 && agendasSeleccionadas.length === 0) {
            setAgendasSeleccionadas(agendas.map((a) => String(a.id || a.agenda_id)));
        }
    }, [agendas, agendasSeleccionadas.length]);

    useEffect(() => {
        if (estadosUnicos.length > 0 && estadosSeleccionados.length === 0) {
            setEstadosSeleccionados(estadosUnicos.map((e) => String(e.estado_smile)));
        }
        if (agendas.length > 0 && estadosUnicos.length > 0 && !catalogosListos) {
            setCatalogosListos(true);
        }
    }, [agendas.length, catalogosListos, estadosSeleccionados.length, estadosUnicos]);

    useEffect(() => {
        if (loggedIn && catalogosListos) {
            cargarEstadisticas();
        }
    }, [loggedIn, catalogosListos]);

    const toggleAgenda = (id) => {
        const sid = String(id);
        setAgendasSeleccionadas((prev) => {
            if (prev.includes(sid)) {
                return prev.filter((x) => x !== sid);
            }
            return [...prev, sid];
        });
    };

    const toggleEstado = (estadoSmile) => {
        const s = String(estadoSmile);
        setEstadosSeleccionados((prev) => {
            if (prev.includes(s)) {
                return prev.filter((x) => x !== s);
            }
            return [...prev, s];
        });
    };

    const seleccionarTodasAgendas = () => {
        setAgendasSeleccionadas(agendas.map((a) => String(a.id || a.agenda_id)));
    };

    const seleccionarTodosEstados = () => {
        setEstadosSeleccionados(estadosUnicos.map((e) => String(e.estado_smile)));
    };

    const exportarExcel = async () => {
        if (totalCitas === 0 && porEstado.length === 0 && porMes.length === 0) {
            alert('No hay datos para exportar');
            return;
        }

        try {
            const params = new URLSearchParams({
                fecha_inicio: fechaInicio,
                fecha_fin: fechaFin,
            });

            if (agendas.length > 0 && agendasSeleccionadas.length > 0 && agendasSeleccionadas.length < agendas.length) {
                params.append('agendas', agendasSeleccionadas.join(','));
            }
            if (estadosUnicos.length > 0 && estadosSeleccionados.length > 0 && estadosSeleccionados.length < estadosUnicos.length) {
                params.append('estados_smile', estadosSeleccionados.join(','));
            }

            const response = await axios.get('/exportar-estadisticas-agenda-excel?' + params.toString(), {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `estadisticas_agenda_${fechaInicio}_${fechaFin}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al exportar:', error);
            alert('Error al exportar: ' + (error.response?.data?.message || error.message));
        }
    };

    const datosPie = useMemo(() => {
        const labels = porEstado.map((r) => r.estado || `Estado ${r.estado_smile}`);
        const values = porEstado.map((r) => Number(r.cantidad_citas || 0));
        const colors = porEstado.map((r) => {
            if (r.color) return r.color;
            const sm = parseInt(r.estado_smile, 10);
            return FALLBACK_COLOR_ESTADO_SMILE[sm] || '#6c757d';
        });

        return {
            labels,
            datasets: [{
                data: values,
                backgroundColor: colors,
                borderWidth: 1,
                borderColor: '#fff',
            }],
        };
    }, [porEstado]);

    const datosLinea = useMemo(() => {
        const labels = porMes.map((r) => {
            const mesIdx = (Number(r.mes) || 1) - 1;
            const nombreMes = MESES_ES[mesIdx] || r.mes;
            return `${nombreMes} ${r.anio}`;
        });
        const values = porMes.map((r) => Number(r.cantidad_citas || 0));

        return {
            labels,
            datasets: [{
                label: 'Citas',
                data: values,
                borderColor: '#17a2b8',
                backgroundColor: 'rgba(23, 162, 184, 0.15)',
                tension: 0.25,
                fill: true,
            }],
        };
    }, [porMes]);

    const opcionesPie = {
        responsive: true,
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const val = ctx.parsed || 0;
                        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                        return `${ctx.label}: ${val} (${pct}%)`;
                    },
                },
            },
        },
    };

    const opcionesLinea = {
        responsive: true,
        plugins: {
            legend: { display: false },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { precision: 0 },
            },
        },
    };

    if (!loggedIn) {
        return <Loading />;
    }

    return (
        <div className="hold-transition">
            <Header client={client} />
            <Aside client={client} activelink="reports" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/reportes-agenda">Reportes de Agenda</Link></li>
                                    <li className="breadcrumb-item active">Estadísticas de Agenda</li>
                                </ol>
                            </div>
                        </div>
                        <div className="row mb-2 align-items-end">
                            <div className="col-md-8">
                                <h1 className="m-0">Estadísticas de Agenda</h1>
                                <p className="text-muted mb-0 small">Distribución de citas por estado y evolución mensual</p>
                            </div>
                            <div className="col-md-4 text-md-right mt-2 mt-md-0">
                                <Link to="/reportes-agenda" className="btn btn-outline-secondary btn-sm">
                                    <i className="fas fa-arrow-left mr-1"></i> Volver
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card card-outline card-info mb-3 estadisticas-agenda-filtros">
                            <div className="card-header">
                                <h3 className="card-title mb-0">Filtros</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="form-group mb-md-0">
                                            <label htmlFor="fechaInicio">Fecha inicial</label>
                                            <input
                                                type="date"
                                                id="fechaInicio"
                                                className="form-control"
                                                value={fechaInicio}
                                                onChange={(e) => setFechaInicio(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group mb-md-0">
                                            <label htmlFor="fechaFin">Fecha final</label>
                                            <input
                                                type="date"
                                                id="fechaFin"
                                                className="form-control"
                                                value={fechaFin}
                                                onChange={(e) => setFechaFin(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6 d-flex align-items-end justify-content-md-end flex-wrap" style={{ gap: '8px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-info"
                                            onClick={cargarEstadisticas}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <><i className="fas fa-spinner fa-spin mr-1"></i> Filtrando...</>
                                            ) : (
                                                <><i className="fas fa-filter mr-1"></i> Filtrar</>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={exportarExcel}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-file-excel mr-1"></i> Exportar Excel
                                        </button>
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <label className="d-block mb-1">
                                            Agendas
                                            <button type="button" className="btn btn-link btn-sm p-0 ml-2 link-todas" onClick={seleccionarTodasAgendas}>
                                                Todas
                                            </button>
                                        </label>
                                        <div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                            {agendas.length === 0 ? (
                                                <span className="text-muted small">No hay agendas disponibles</span>
                                            ) : (
                                                agendas.map((agenda) => {
                                                    const id = String(agenda.id || agenda.agenda_id);
                                                    const checked = agendasSeleccionadas.includes(id);
                                                    return (
                                                        <label key={id} className="d-block mb-1 small" style={{ cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="mr-2"
                                                                checked={checked}
                                                                onChange={() => toggleAgenda(id)}
                                                            />
                                                            {agenda.nombre || agenda.nombre_agenda}
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                        <small className="text-muted">Desmarque para excluir agendas del reporte</small>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="d-block mb-1">
                                            Estados de cita
                                            <button type="button" className="btn btn-link btn-sm p-0 ml-2 link-todas" onClick={seleccionarTodosEstados}>
                                                Todos
                                            </button>
                                        </label>
                                        <div className="border rounded p-2" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                            {estadosUnicos.length === 0 ? (
                                                <span className="text-muted small">No hay estados configurados</span>
                                            ) : (
                                                estadosUnicos.map((estado) => {
                                                    const sm = String(estado.estado_smile);
                                                    const checked = estadosSeleccionados.includes(sm);
                                                    const color = estado.color || FALLBACK_COLOR_ESTADO_SMILE[estado.estado_smile] || '#6c757d';
                                                    return (
                                                        <label key={sm} className="d-block mb-1 small" style={{ cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="mr-2"
                                                                checked={checked}
                                                                onChange={() => toggleEstado(sm)}
                                                            />
                                                            <span
                                                                className="d-inline-block rounded-circle mr-1"
                                                                style={{ width: 10, height: 10, backgroundColor: color }}
                                                            />
                                                            {estado.nombre}
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                        <small className="text-muted">Desmarque para excluir estados del reporte</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="alert alert-light border mb-0 py-2">
                                    <strong>Total citas en el período:</strong> {totalCitas.toLocaleString('es-CO')}
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-6 mb-4">
                                <div className="card h-100">
                                    <div className="card-header">
                                        <h3 className="card-title mb-0">Citas por estado</h3>
                                    </div>
                                    <div className="card-body centrar bloque-grafico">
                                        {porEstado.length > 0 && datosPie.datasets[0].data.some((v) => v > 0) ? (
                                            <Pie data={datosPie} options={opcionesPie} />
                                        ) : (
                                            <p className="text-muted text-center mb-0 py-5">No hay citas para los filtros seleccionados</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 mb-4">
                                <div className="card h-100">
                                    <div className="card-header">
                                        <h3 className="card-title mb-0">Citas por mes</h3>
                                    </div>
                                    <div className="card-body centrar bloque-grafico">
                                        {porMes.length > 0 ? (
                                            <Line data={datosLinea} options={opcionesLinea} />
                                        ) : (
                                            <p className="text-muted text-center mb-0 py-5">No hay citas para los filtros seleccionados</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer showToast={false} delay={0} />
            <style>{`
                .estadisticas-agenda-filtros input[type="checkbox"] {
                    accent-color: #067993;
                    width: 15px;
                    height: 15px;
                    cursor: pointer;
                }
                .estadisticas-agenda-filtros .link-todas {
                    color: #067993 !important;
                    text-decoration: none;
                    font-weight: 600;
                }
                .estadisticas-agenda-filtros .link-todas:hover {
                    color: #06677d !important;
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
}

export default EstadisticasAgenda;
